import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import SectionProgress from '~/pages/anatomyV2/libs/components/SectionProgress'
import {
  ITextOption,
  correctMascotPNG,
  correctMascotWEBP,
  incorrectMascotPNG,
  incorrectMascotWEBP,
} from '~/pages/anatomyV2/libs/components/TextOptionsQuestionTemplate/constants'
import { TextOptionsQuestionTemplateStyles as S } from '~/pages/anatomyV2/libs/components/TextOptionsQuestionTemplate/styles'
import { useMatchHeaderBackground } from '~/pages/anatomyV2/libs/hooks/useMatchHeaderBackground'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { TPageProps } from '~/shared/types/commonTypes'

interface ITextOptionsQuestionTemplateProps extends TPageProps {
  currentSection: number
  titleI18nKey: string
  questionImagePNG: string
  questionImageWEBP: string
  questionImageAlt: string
  options: ITextOption[]
  correctOptionKey: string
  correctTitleI18nKey: string
  correctExplanationI18nKey: string
  incorrectTitleI18nKey: string
  incorrectExplanationI18nKey: string
  question: string
}

const TextOptionsQuestionTemplate = ({
  pageId,
  nextPagePath,
  currentSection,
  titleI18nKey,
  questionImagePNG,
  questionImageWEBP,
  questionImageAlt,
  options,
  correctOptionKey,
  correctTitleI18nKey,
  correctExplanationI18nKey,
  incorrectTitleI18nKey,
  incorrectExplanationI18nKey,
  question,
}: ITextOptionsQuestionTemplateProps) => {
  useMatchHeaderBackground()

  const { t } = useTranslation('anatomyV2')
  const [answer, setAnswer] = useState<string | null>(null)

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question,
  })

  const isCorrect = answer === correctOptionKey

  return (
    <S.Root>
      <S.ContentWrapper>
        <SectionProgress currentSection={currentSection} pageId={pageId} />

        <S.Title>
          <Trans ns="anatomyV2" i18nKey={titleI18nKey} />
        </S.Title>

        {answer === null ? (
          <>
            <picture>
              <source srcSet={questionImageWEBP} type="image/webp" />
              <S.Image src={questionImagePNG} alt={questionImageAlt} />
            </picture>

            <S.Options>
              {options.map(({ key, i18nKey }) => (
                <S.OptionButton
                  key={key}
                  type="button"
                  onClick={() => setAnswer(key)}
                >
                  {t(i18nKey)}
                </S.OptionButton>
              ))}
            </S.Options>
          </>
        ) : (
          <>
            <S.ResultContent>
              <S.ResultCard $isCorrect={isCorrect}>
                <picture>
                  <source
                    srcSet={isCorrect ? correctMascotWEBP : incorrectMascotWEBP}
                    type="image/webp"
                  />
                  <S.ResultMascot
                    src={isCorrect ? correctMascotPNG : incorrectMascotPNG}
                    alt=""
                  />
                </picture>

                <S.ResultTitle $isCorrect={isCorrect}>
                  <Trans
                    ns="anatomyV2"
                    i18nKey={
                      isCorrect ? correctTitleI18nKey : incorrectTitleI18nKey
                    }
                  />
                </S.ResultTitle>

                <S.ResultExplanation>
                  <Trans
                    ns="anatomyV2"
                    i18nKey={
                      isCorrect
                        ? correctExplanationI18nKey
                        : incorrectExplanationI18nKey
                    }
                  />
                </S.ResultExplanation>
              </S.ResultCard>
            </S.ResultContent>

            <S.StyledButton
              type="button"
              onClick={() => handleContinue(answer)}
            >
              <Trans i18nKey="actions.continue" />
            </S.StyledButton>
          </>
        )}
      </S.ContentWrapper>
    </S.Root>
  )
}

export default TextOptionsQuestionTemplate
